import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  FolderOpen as FolderIcon,
  PhoneAndroid as AndroidIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  NewReleases as NewIcon,
  LinkOff as DisconnectIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import type { ActiveVersion } from '../../types/AppMobileTypes';
import {
  isFileSystemAccessSupported,
  saveDirectoryHandle,
  getDirectoryHandle,
  clearDirectoryHandle,
  requestPermission,
  scanForApk,
  type LocalApkInfo,
} from '../../services/localApkService';

interface LocalApkDetectorProps {
  activeVersion: ActiveVersion | null;
  onFileReady: (file: File) => void;
  disabled?: boolean;
  appLabel?: string;
  storageKey?: string;
  /** Come si chiama l'APK dell'app scelta: senza, si cercherebbe sempre quello di YumaCALL. */
  apkFileName: string;
}

function formatBytes(bytes: number): string {
  if (!bytes) return '-';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type ComparisonResult = 'newer' | 'same' | 'unknown';

function compareWithPublished(
  apk: LocalApkInfo,
  published: ActiveVersion | null
): ComparisonResult {
  if (!published) return 'unknown';

  const publishedTime = published.published_at?.toDate?.()
    ? published.published_at.toDate().getTime()
    : 0;

  if (apk.lastModified > publishedTime) return 'newer';

  if (
    apk.size === published.apkSizeBytes &&
    Math.abs(apk.lastModified - publishedTime) < 60_000
  ) {
    return 'same';
  }

  if (apk.lastModified > publishedTime) return 'newer';

  return 'same';
}

export const LocalApkDetector: React.FC<LocalApkDetectorProps> = ({
  activeVersion,
  onFileReady,
  disabled = false,
  appLabel = 'YumaCALL',
  // ⚠️ QUESTE DUE ARRIVAVANO DALLA PAGINA E VENIVANO BUTTATE VIA. Erano dichiarate fra le
  // proprietà e non raccolte qui: il componente continuava a usare la cartella e il nome file
  // di YumaCALL per tutte e tre le app. Sembrava già risolto, e non lo era.
  storageKey,
  apkFileName,
}) => {
  const [supported] = useState(() => isFileSystemAccessSupported());
  const [connected, setConnected] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [apkInfo, setApkInfo] = useState<LocalApkInfo | null>(null);
  const [dirName, setDirName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const doScan = useCallback(async (handle: FileSystemDirectoryHandle) => {
    setScanning(true);
    setError(null);
    setApkInfo(null);

    try {
      const result = await scanForApk(handle, apkFileName);
      setApkInfo(result);
      if (!result) {
        setError('Nessun APK trovato nella cartella del progetto. Esegui una build prima.');
      }
    } catch (err) {
      console.error('Errore scansione APK:', err);
      setError('Errore durante la scansione della cartella');
    } finally {
      setScanning(false);
    }
  }, []);

  useEffect(() => {
    if (!supported) return;

    let cancelled = false;

    (async () => {
      try {
        const handle = await getDirectoryHandle(storageKey);
        if (!handle || cancelled) return;

        const granted = await requestPermission(handle);
        if (!granted || cancelled) return;

        setConnected(true);
        setDirName(handle.name);
        await doScan(handle);
      } catch {
        // Nessun handle salvato o permesso negato
      }
    })();

    return () => { cancelled = true; };
  }, [supported, doScan]);

  const handleConnect = useCallback(async () => {
    setError(null);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const handle = await (window as any).showDirectoryPicker({ mode: 'read' });
      await saveDirectoryHandle(handle, storageKey);
      setConnected(true);
      setDirName(handle.name);
      await doScan(handle);
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        setError('Errore nella selezione della cartella');
      }
    }
  }, [doScan]);

  const handleDisconnect = useCallback(async () => {
    await clearDirectoryHandle(storageKey);
    setConnected(false);
    setApkInfo(null);
    setDirName(null);
    setError(null);
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
      const handle = await getDirectoryHandle(storageKey);
      if (!handle) return;

      const granted = await requestPermission(handle);
      if (!granted) {
        setError('Permesso di lettura negato. Riconnetti la cartella.');
        setConnected(false);
        return;
      }

      await doScan(handle);
    } catch {
      setError('Errore durante il refresh');
    }
  }, [doScan]);

  const handleUpload = useCallback(() => {
    if (apkInfo) {
      onFileReady(apkInfo.file);
    }
  }, [apkInfo, onFileReady]);

  if (!supported) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'grey.50',
          borderStyle: 'dashed',
        }}
      >
        <FolderIcon sx={{ color: 'grey.400', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={500} color="text.secondary">
            Rilevamento APK automatico non disponibile
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Accedi tramite <strong>localhost</strong> con Chrome per abilitare l'accesso al filesystem locale.
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!connected) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'grey.50',
          borderStyle: 'dashed',
        }}
      >
        <FolderIcon sx={{ color: 'grey.500', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            Rilevamento APK automatico
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Collega la cartella del progetto Android di {appLabel} per rilevare automaticamente
            le sue build
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FolderIcon />}
          onClick={handleConnect}
        >
          Collega cartella
        </Button>

        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Paper>
    );
  }

  const comparison = apkInfo ? compareWithPublished(apkInfo, activeVersion) : null;

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <FolderIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {dirName}/
        </Typography>
        <Tooltip title="Aggiorna scansione">
          <IconButton size="small" onClick={handleRefresh} disabled={scanning}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Scollega cartella">
          <IconButton size="small" onClick={handleDisconnect}>
            <DisconnectIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ p: 2 }}>
        {scanning && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Scansione APK in corso...
            </Typography>
          </Box>
        )}

        {!scanning && error && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
            <AndroidIcon sx={{ color: 'grey.400' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {error}
              </Typography>
            </Box>
            <Button size="small" variant="text" onClick={handleConnect}>
              Cambia cartella
            </Button>
          </Box>
        )}

        <Collapse in={!scanning && !!apkInfo}>
          {apkInfo && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AndroidIcon
                sx={{
                  fontSize: 36,
                  color: comparison === 'newer' ? '#ff9800' : '#4caf50',
                }}
              />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                  <Typography variant="body1" fontWeight={600} noWrap>
                    {apkFileName}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {formatBytes(apkInfo.size)} &middot; {formatDate(apkInfo.lastModified)}
                </Typography>

                {comparison === 'newer' && (
                  <Chip
                    icon={<NewIcon />}
                    label="Nuova build disponibile"
                    color="warning"
                    size="small"
                    sx={{ mt: 0.5, height: 22, fontSize: 11 }}
                  />
                )}
                {comparison === 'same' && (
                  <Chip
                    icon={<CheckIcon />}
                    label="APK gia aggiornato"
                    color="success"
                    variant="outlined"
                    size="small"
                    sx={{ mt: 0.5, height: 22, fontSize: 11 }}
                  />
                )}
              </Box>

              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={handleUpload}
                disabled={disabled}
                sx={{
                  bgcolor: '#4caf50',
                  '&:hover': { bgcolor: '#388e3c' },
                  whiteSpace: 'nowrap',
                }}
              >
                Carica APK
              </Button>
            </Box>
          )}
        </Collapse>
      </Box>
    </Paper>
  );
};
